import { module as BaseModule } from 'modujs'
import { load } from 'recaptcha-v3'

const MIN_SUBMIT_MS = 1200

export default class extends BaseModule {
  init() {
    this.email = this.el.querySelector('[data-newsletter-email]')
    this.submit = this.el.querySelector('[data-newsletter-submit]')
    this.status = this.el.querySelector('[data-newsletter-status]')

    if (!this.email || !this.submit || !this.status) {
      return
    }

    this.isSubmitting = false
    this.successMessage = this.el.dataset.newsletterSuccess || ''
    this.errorMessage = this.el.dataset.newsletterError || ''
    this.siteKey = this.el.dataset.newsletterRecaptchaSiteKey || ''
    this.verifyUrl = this.el.dataset.newsletterVerifyUrl || '/wp-json/custom/v1/newsletter'
    this.readyAt = Date.now() + MIN_SUBMIT_MS

    this.onSubmit = this.onSubmit.bind(this)
    this.onInput = this.onInput.bind(this)

    this.el.addEventListener('submit', this.onSubmit)
    this.email.addEventListener('input', this.onInput)
  }

  onInput() {
    if (this.el.classList.contains('is-success')) {
      return
    }

    this.clearStatus()
    this.email.removeAttribute('aria-invalid')
  }

  async onSubmit(event) {
    event.preventDefault()

    if (this.isSubmitting || this.el.classList.contains('is-success')) {
      return
    }

    if (!this.el.reportValidity()) {
      return
    }

    // Soft bot gate: ignore instant automated submits.
    if (Date.now() < this.readyAt) {
      this.showStatus(this.errorMessage, 'error')
      return
    }

    this.isSubmitting = true
    this.clearStatus()

    // Build payload before disabling controls — FormData omits disabled fields.
    const payload = new FormData(this.el)
    this.setLoading(true)

    try {
      await this.verifyRecaptcha()
      await this.submitToBrevo(payload)
    } catch {
      this.email.setAttribute('aria-invalid', 'true')
      this.showStatus(this.errorMessage, 'error')
      this.isSubmitting = false
      this.setLoading(false)
    }
  }

  async verifyRecaptcha() {
    if (!this.siteKey) {
      throw new Error('Missing reCAPTCHA site key')
    }

    const recaptcha = await load(this.siteKey)
    const token = await recaptcha.execute('newsletter')

    const response = await fetch(this.verifyUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ token }),
    })

    let data = null
    try {
      data = await response.json()
    } catch {
      data = null
    }

    if (!response.ok || data?.success === false) {
      throw new Error(data?.message || 'reCAPTCHA verification failed')
    }
  }

  async submitToBrevo(payload) {
    const endpoint = new URL(this.el.action)
    endpoint.searchParams.set('isAjax', '1')

    const response = await fetch(endpoint.toString(), {
      method: 'POST',
      body: payload,
      headers: {
        Accept: 'application/json',
      },
    })

    let data = null
    try {
      data = await response.json()
    } catch {
      data = null
    }

    if (response.ok && data?.success) {
      this.el.reset()
      this.email.removeAttribute('aria-invalid')
      this.el.classList.add('is-success')
      this.showStatus(this.successMessage || data.message, 'success')
      this.isSubmitting = false
      this.setLoading(false)
      return
    }

    const serverMessage =
      data?.message ||
      (data?.errors &&
        Object.values(data.errors)
          .map((value) => (Array.isArray(value) ? value[0] : value))
          .find(Boolean))

    this.email.setAttribute('aria-invalid', 'true')
    this.showStatus(serverMessage || this.errorMessage, 'error')
    this.isSubmitting = false
    this.setLoading(false)
  }

  setLoading(isLoading) {
    this.el.classList.toggle('is-loading', isLoading)
    this.submit.disabled = isLoading
    this.submit.setAttribute('aria-busy', isLoading ? 'true' : 'false')
    this.email.disabled = isLoading
  }

  showStatus(message, type) {
    if (!message) {
      this.clearStatus()
      return
    }

    this.status.hidden = false
    this.status.textContent = message
    this.status.classList.toggle('is-success', type === 'success')
    this.status.classList.toggle('is-error', type === 'error')
    this.email.setAttribute('aria-describedby', this.ensureStatusId())
  }

  ensureStatusId() {
    if (!this.status.id) {
      this.status.id = 'footer-newsletter-status'
    }
    return this.status.id
  }

  clearStatus() {
    this.status.hidden = true
    this.status.textContent = ''
    this.status.classList.remove('is-success', 'is-error')
    this.email.removeAttribute('aria-describedby')
    this.el.classList.remove('is-success')
  }

  destroy() {
    this.el.removeEventListener('submit', this.onSubmit)
    this.email?.removeEventListener('input', this.onInput)
  }
}
